// KEEP IN SYNC with ./charts-endtag.d.mts.
/**
 * Where a series' end tag goes.
 *
 * The tag labels the last value of a hero series, so it wants to sit just
 * past that point — but the last point of a full-width series is ON the
 * plot's right edge, and there is only `PAD.right` (14px) of canvas beyond
 * it. Preferring the right and flipping left when it does not fit is the
 * right instinct and was the shipped logic; what it lacked was the admission
 * that *neither* side always fits. Without a final clamp the tag was drawn
 * past the canvas and simply vanished — invisible on a wide chart, cut off
 * mid-number on a narrow one, and silent either way because a canvas draws
 * out of bounds without complaining.
 *
 * So: prefer right, flip left, then clamp. The clamp is what makes this
 * correct by construction rather than correct for the geometries someone
 * happened to try.
 *
 * @param {{ px:number, py:number, w:number, h:number, gap:number,
 *           plot:{x:number,y:number,width:number,height:number} }} spec
 * @returns {{ x:number, y:number }} top-left of the tag box
 */
export function endTagBox(spec) {
  const { px, py, w, h, gap, plot } = spec;
  const left = plot.x;
  const right = plot.x + plot.width;

  // Prefer trailing the point; flip when the tag would run past the plot.
  let x = px + gap + w > right ? px - gap - w : px + gap;

  // Neither side fitting is not a licence to overflow. Clamp last so the box
  // is inside the plot no matter what px was — a band centre can land on or
  // slightly past the edge, and a plot can be narrower than the tag.
  if (x + w > right) x = right - w;
  if (x < left) x = left;

  // Same argument vertically: a last value at the very top or bottom of the
  // range would otherwise be labelled by a half-drawn box.
  let y = py - h / 2;
  const bottom = plot.y + plot.height;
  if (y + h > bottom) y = bottom - h;
  if (y < plot.y) y = plot.y;

  return { x, y };
}
